import { Component, Input, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import {
  ArchivosExpedientesService,
  ID_CREDENCIAL_ELECTOR,
} from 'src/app/administracion/servicios/archivos-expedientes';

@Component({
  selector: 'visor-ine',
  template: `
    <div id="contenedor-ine">
      <header>Credencial de Elector (INE)</header>
      <main>
        <ng-container *ngIf="cargando">
          <div class="cargando">
            <mat-spinner diameter="40"></mat-spinner>
          </div>
        </ng-container>
        <ng-container *ngIf="error">
          <div class="error">
            {{ error }}
          </div>
        </ng-container>
        <ng-container *ngIf="archivo">
          <div id="toolbar">
            <button mat-icon-button (click)="zoomOut()">
              <mat-icon>remove</mat-icon>
            </button>
            <button mat-icon-button (click)="zoomIn()">
              <mat-icon>add</mat-icon>
            </button>

            <button mat-icon-button (click)="rotateLeft()">
              <mat-icon>undo</mat-icon>
            </button>

            <button mat-icon-button (click)="rotateRight()">
              <mat-icon>redo</mat-icon>
            </button>
          </div>

          <mat-divider></mat-divider>

          <div id="pdf-container">
            <pdf-viewer
              [src]="archivo"
              [zoom]="zoom"
              [rotation]="rotation"
            ></pdf-viewer>
          </div>
        </ng-container>
      </main>
    </div>
  `,
  styles: [
    `
      @use '~@angular/material' as mat;

      #contenedor-ine {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;

        header {
          font-size: larger;
          font-weight: 500;
          padding: 0.5rem 1rem;
        }

        main {
          padding: 0 1rem;
          display: flex;
          flex-direction: column;
          flex: 1;
        }

        #toolbar {
          display: flex;
          gap: 0.5rem;
        }

        #pdf-container {
          flex: 1;
          pdf-viewer {
            width: 100%;
            height: 100%;
          }
        }
      }

      .error {
        background-color: mat.get-color-from-palette(mat.$red-palette, 100);
        border-radius: 0.25rem;
        color: mat.get-color-from-palette(mat.$red-palette, 900);
        font-weight: 500;
        padding: 1rem;
      }

      .cargando {
        align-items: center;
        display: flex;
        justify-content: center;
        padding: 1rem;
      }
    `,
  ],
})
export class VisorIneComponent implements OnDestroy {
  private readonly onDestroy$ = new Subject<void>();

  public cargando: boolean = false;
  public error: string | null = null;
  public archivo: Uint8Array | null = null;

  public zoom = 0;
  public rotation = 0;

  @Input()
  set idEmpleado(idEmpleado: number) {
    this.obtenerINE(idEmpleado);
  }

  constructor(
    private readonly archivosExpedientesService: ArchivosExpedientesService,
  ) {}

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  obtenerINE(idEmpleado: number) {
    this.cargando = true;
    this.error = null;
    this.archivo = null;

    this.archivosExpedientesService
      .obtenerPorEmpleado(idEmpleado, ID_CREDENCIAL_ELECTOR)
      .pipe(
        finalize(() => (this.cargando = false)),
        takeUntil(this.onDestroy$),
      )
      .subscribe({
        next: async (archivo) => {
          try {
            const buffer = await archivo.arrayBuffer();
            this.archivo = new Uint8Array(buffer);
          } catch {
            this.error = 'Error al procesar la credencial de elector';
          }
        },
        error: (error) => {
          if (error.status === 404) {
            this.error = 'No se encontró el documento para este empleado';
            return;
          }

          this.error = 'Error al obtener la credencial de elector';
        },
      });
  }

  zoomIn() {
    this.zoom += 0.25;
  }

  zoomOut() {
    this.zoom = Math.max(this.zoom - 0.25, 0.25);
  }

  rotateLeft() {
    this.rotation = (this.rotation - 90) % 360;
  }

  rotateRight() {
    this.rotation = (this.rotation + 90) % 360;
  }
}
